import { useState } from "react";
import { toast } from "sonner";
import { Share2, Twitter, Instagram, Link2, Loader2, X } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import api from "../lib/api";

export default function SocialShare({ 
  type, // 'league', 'tournament', 'profile'
  id, // leagueId, tournamentId, username
  title,
  onClose 
}) {
  const [loading, setLoading] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [open, setOpen] = useState(true);

  const fetchShareData = async () => {
    setLoading(true);
    try {
      let response;
      if (type === 'league') {
        response = await api.getLeagueShareData(id);
      } else if (type === 'tournament') {
        response = await api.getTournamentShareData(id);
      } else if (type === 'profile') {
        response = await api.getProfileShareData(id);
      }
      setShareData(response.data);
    } catch (error) {
      console.error("Error fetching share data:", error);
      toast.error("Paylaşım verisi alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useState(() => {
    fetchShareData();
  }, []);

  const handleShare = async (platform) => {
    if (!shareData) return;

    // Track share
    try {
      await api.trackShare({
        content_type: type,
        content_id: id,
        platform
      });
    } catch (e) {
      console.error("Track error:", e);
    }

    if (platform === 'twitter') {
      window.open(shareData.twitter_url, '_blank', 'width=600,height=400');
      toast.success("Twitter'da paylaşıldı!");
    } else if (platform === 'instagram') {
      // Instagram doesn't support direct sharing, copy text instead
      navigator.clipboard.writeText(shareData.share_text + "\n\n" + shareData.share_url);
      toast.success("Metin kopyalandı! Instagram'da paylaşabilirsiniz.");
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(shareData.share_url);
      toast.success("Link kopyalandı!");
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0A1635] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#4DFFFF]" />
            Paylaş
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-[#4DFFFF] animate-spin" />
          </div>
        ) : shareData ? (
          <div className="space-y-4 py-4">
            {/* Preview */}
            <div className="p-4 rounded-lg bg-white/5">
              <p className="text-white font-medium mb-2">{shareData.title || title}</p>
              <p className="text-sm text-[#B1C1C1] whitespace-pre-line line-clamp-5">
                {shareData.share_text}
              </p>
            </div>

            {/* Share Buttons */}
            <div className="grid grid-cols-3 gap-3">
              <Button
                onClick={() => handleShare('twitter')}
                className="flex flex-col items-center gap-2 h-auto py-4 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border border-[#1DA1F2]/30"
                variant="outline"
                data-testid="share-twitter-btn"
              >
                <Twitter className="w-6 h-6 text-[#1DA1F2]" />
                <span className="text-xs text-[#1DA1F2]">Twitter</span>
              </Button>

              <Button
                onClick={() => handleShare('instagram')}
                className="flex flex-col items-center gap-2 h-auto py-4 bg-[#E4405F]/10 hover:bg-[#E4405F]/20 border border-[#E4405F]/30"
                variant="outline"
                data-testid="share-instagram-btn"
              >
                <Instagram className="w-6 h-6 text-[#E4405F]" />
                <span className="text-xs text-[#E4405F]">Instagram</span>
              </Button>

              <Button
                onClick={() => handleShare('copy')}
                className="flex flex-col items-center gap-2 h-auto py-4 bg-white/5 hover:bg-white/10 border border-white/20"
                variant="outline"
                data-testid="share-copy-btn"
              >
                <Link2 className="w-6 h-6 text-white" />
                <span className="text-xs text-white">Kopyala</span>
              </Button>
            </div>

            {/* Share URL */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5">
              <input
                type="text"
                value={shareData.share_url}
                readOnly
                className="flex-1 bg-transparent text-sm text-[#B1C1C1] outline-none"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  navigator.clipboard.writeText(shareData.share_url);
                  toast.success("Link kopyalandı!");
                }}
                className="text-[#4DFFFF]"
              >
                Kopyala
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-[#64748B]">
            Paylaşım verisi yüklenemedi
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
